import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
import osName from "../../../OsName";

const ROTATION_STRENGTH = osName() == 'PC' ? 0.01 : 0.02;
const ANIM_DURATION = 300;
// 横向死区：移动超过此值才开始视觉跟随
const DRAG_DEAD_ZONE = 10;

const styles = {
  segment:{
    position: "absolute",
    top: "18px",
    right: "25px",
    display: "flex",
    alignItems: "center",
    gap: "3.5px",
    zIndex: 10,
    pointerEvents: "none",
  },
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    background: "transparent",
    overflow: "hidden",
    userSelect: "none",
    // 允许纵向滚动，横向由组件自己处理
    touchAction: "pan-y",
    position: "relative",
  },
  container: {
    width: "100%",
    maxWidth: "720px",
    position: "relative",
    height: "auto",
    cursor: "grab",
  },
  card: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  }
};

// 拆分出内部组件，确保 hooks 调用顺序始终一致
function HomeCardsInner({ data, renderItem }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animType, setAnimType] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const dragXRef = useRef(0);
  const dragYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isAnimatingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const autoPlayTimerRef = useRef(null);
  const animTimerRef = useRef(null);
  const countRef = useRef(data.length);

  const flyOutDistance = useMemo(() => {
    return osName() === 'PC' ? 750 : 400;
  }, []);

  const count = data.length;
  countRef.current = count;

  const clearAutoPlay = useCallback(() => {
    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
  }, []);

  const clearAnimTimer = useCallback(() => {
    if (animTimerRef.current) {
      clearTimeout(animTimerRef.current);
      animTimerRef.current = null;
    }
  }, []);

  const triggerTransition = useCallback((dir) => {
    // 用 ref 做同步锁，避免卡顿/连点时重复进入导致 index 与图层错位
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    setIsAnimating(true);
    setAnimType(dir);

    const targetX = dir === -1 ? -flyOutDistance : flyOutDistance;
    dragXRef.current = targetX;
    setDragX(targetX);

    clearAnimTimer();
    animTimerRef.current = setTimeout(() => {
      animTimerRef.current = null;
      isAnimatingRef.current = false;
      setIsAnimating(false);
      setAnimType(0);
      dragXRef.current = 0;
      setDragX(0);
      setActiveIndex(prev => (prev - dir + countRef.current) % countRef.current);
    }, ANIM_DURATION);
  }, [flyOutDistance, clearAnimTimer]);

  const startAutoPlay = useCallback(() => {
    clearAutoPlay();
    if (countRef.current > 1) {
      autoPlayTimerRef.current = setTimeout(() => {
        // 拖拽或动画中不自动切页
        if (isDraggingRef.current || isAnimatingRef.current) return;
        triggerTransition(-1);
      }, 3000);
    }
  }, [clearAutoPlay, triggerTransition]);

  useEffect(() => {
    startAutoPlay();
    return () => {
      clearAutoPlay();
      clearAnimTimer();
    };
  }, [activeIndex, count, startAutoPlay, clearAutoPlay, clearAnimTimer]);

  const handlePointerDown = (e) => {
    if (isAnimatingRef.current || count <= 1) return;
    if (e.isPrimary === false) return;
    clearAutoPlay();
    isDraggingRef.current = true;
    setIsDragging(true);
    hasDraggedRef.current = false;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    dragYRef.current = 0;
    // 不在 pointerdown 立即 capture，等超出死区后再 capture
    // 这样浏览器的 touchAction:pan-y 能正常接管纵向/斜向手势并触发 pointercancel
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    if (e.isPrimary === false) return;

    const deltaX = e.clientX - startXRef.current;
    const deltaY = e.clientY - startYRef.current;

    // 超出横向死区才开始跟随，同时 capture 指针防止后续丢失
    if (!hasDraggedRef.current) {
      if (Math.abs(deltaX) < DRAG_DEAD_ZONE) return;
      hasDraggedRef.current = true;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    }

    dragXRef.current = deltaX;
    dragYRef.current = deltaY;
    setDragX(deltaX);
  };

  const handlePointerUp = (e) => {
    if (!isDraggingRef.current) return;
    if (e.isPrimary === false) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}

    const currentDragX = dragXRef.current;
    const currentDragY = dragYRef.current;

    // 未超出死区（纯点击或竖向被 pointercancel）直接重置
    if (!hasDraggedRef.current) {
      dragXRef.current = 0;
      setDragX(0);
      startAutoPlay();
      return;
    }

    // 只要横向主导（|deltaX| > |deltaY|）就直接完成翻页，不需要最小距离
    const isHorizontalSwipe = Math.abs(currentDragX) > Math.abs(currentDragY);
    if (isHorizontalSwipe && currentDragX < 0) {
      triggerTransition(-1);
    } else if (isHorizontalSwipe && currentDragX > 0) {
      triggerTransition(1);
    } else {
      // 非横向手势（竖滑/斜滑）snap-back：保留方向让卡片沿原路退回，不穿透 banner
      const snapDir = currentDragX > 0 ? 1 : currentDragX < 0 ? -1 : 0;
      isAnimatingRef.current = true;
      setIsAnimating(true);
      setAnimType(snapDir);
      dragXRef.current = 0;
      setDragX(0);
      clearAnimTimer();
      animTimerRef.current = setTimeout(() => {
        animTimerRef.current = null;
        isAnimatingRef.current = false;
        setIsAnimating(false);
        setAnimType(0);
        startAutoPlay();
      }, 300);
    }
  };

  const nextIdx = (activeIndex + 1) % count;
  const prevIdx = (activeIndex - 1 + count) % count;

  // 死区/动画外不驱动视觉
  const effectiveDragX = (hasDraggedRef.current || isAnimating) ? dragX : 0;

  // renderDir：拖拽时跟 effectiveDragX，动画/snap-back 时跟 animType
  const renderDir = effectiveDragX !== 0
    ? Math.sign(effectiveDragX)
    : Math.sign(animType);

  // 飞入卡片渲染在 DOM 最后 = 天然最上层，不依赖 zIndex 排序
  const rightSwipe = renderDir > 0;
  const layers = count === 1
    ? [{ cardIdx: activeIndex, role: "current" }]
    : count === 2
      ? rightSwipe
        ? [{ cardIdx: activeIndex, role: "current" }, { cardIdx: nextIdx, role: "next" }]
        : [{ cardIdx: nextIdx, role: "next" }, { cardIdx: activeIndex, role: "current" }]
      : rightSwipe
        ? [{ cardIdx: nextIdx, role: "next" }, { cardIdx: activeIndex, role: "current" }, { cardIdx: prevIdx, role: "prev" }]
        : [{ cardIdx: prevIdx, role: "prev" }, { cardIdx: nextIdx, role: "next" }, { cardIdx: activeIndex, role: "current" }];

  return (
    <div style={styles.wrapper}>
      <div 
        style={{
          ...styles.container,
          cursor: isDragging ? "grabbing" : "grab"
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div style={{ visibility: "hidden", pointerEvents: "none", width: "100%" }}>
          {renderItem ? renderItem(data[activeIndex], activeIndex) : null}
        </div>

        {layers.map(({ cardIdx, role }) => {
          const item = data[cardIdx];
          const isNext = role === "next";
          const isCurrent = role === "current";
          const isPrev = role === "prev";

          let transform = "";
          let opacity = 1;
          // z-index 必须显式设置，目的是为每张卡建立独立 stacking context，
          // 防止卡片内部（HomeBanner 等）的 z-index 泄漏到父级，压住飞入的卡片
          let zIndex = 1;

          const progress = Math.min(Math.abs(effectiveDragX) / (animType !== 0 ? flyOutDistance : 200), 1);

          if (renderDir <= 0) {
            // 左滑 / snap-back 向左 / 静止：current 在最上层飞出
            if (isPrev) {
              opacity = 0;
              zIndex = 0;
            } else if (isNext) {
              zIndex = 1;
              transform = `scale(${0.95 + progress * 0.05})`;
              opacity = progress;
            } else if (isCurrent) {
              zIndex = 2;
              transform = `translateX(${effectiveDragX}px) rotate(${effectiveDragX * ROTATION_STRENGTH}deg)`;
            }
          } else {
            // 右滑 / snap-back 向右：prev 在最上层飞入
            if (isNext && count > 2) {
              opacity = 0;
              zIndex = 0;
            } else if (isPrev || (count === 2 && isNext)) {
              zIndex = 2;
              // snap-back 时 effectiveDragX=0，followX=-flyOutDistance，prev 退回屏幕外
              const followX = -flyOutDistance + effectiveDragX;
              transform = `translateX(${followX}px) rotate(${followX * ROTATION_STRENGTH}deg)`;
            } else if (isCurrent) {
              zIndex = 1;
              transform = `scale(${1 - progress * 0.05})`;
            }
          }

          // isFlying 用 renderDir，确保 snap-back 时过渡动画也能触发
          const isFlying =
            (renderDir <= 0 && isCurrent) ||
            (renderDir > 0 && (isPrev || (count === 2 && isNext)));

          return (
            <div
              // 用 cardIdx 保活实例：滑完 activeIndex 切换时 SvgPath/SvgCurve 不 remount，避免路径重测闪烁
              key={cardIdx}
              style={{
                ...styles.card,
                transform,
                opacity,
                zIndex,
                pointerEvents: isDragging && hasDraggedRef.current ? "none" : "auto",
                transition: isAnimating
                  ? isFlying
                    ? `transform ${ANIM_DURATION}ms cubic-bezier(0.42, 0, 1, 1)`
                    : `transform ${ANIM_DURATION}ms cubic-bezier(0.42, 0, 1, 1), opacity ${ANIM_DURATION}ms ease-out`
                  : "none",
              }}
            >
              {renderItem ? renderItem(item, cardIdx) : null}
            </div>
          );
        })}
      </div>
      {data.length > 1 && (
        <div style={styles.segment}>
          {data.map((_, index) => (
            <img key={index} src={`resources/250627/${activeIndex === index ? "swiper-active" : "swiper-normal"}.png`} alt="" height="7"/>
          ))}
        </div>
      )}
    </div>
  );
}

// 外层包装组件处理空数据的早返回，内层组件 hooks 顺序永远一致
function HomeCards({ data = [], renderItem }) {
  if (!data || data.length === 0) return null;
  return <HomeCardsInner data={data} renderItem={renderItem} />;
}

export default HomeCards;